import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, Trash2, RefreshCw, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

interface GuestRow {
  id: number;
  contractId: number;
  email: string;
  status: "pending" | "active" | "revoked" | "expired";
  invitedByName: string | null;
  expiresAt: string;
  invitedAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
}

interface Props {
  contractId: number;
}

const STATUS_BADGE: Record<GuestRow["status"], "default" | "secondary" | "destructive" | "outline"> = {
  pending: "secondary",
  active: "default",
  revoked: "destructive",
  expired: "outline",
};

export function ContractGuestsPanel({ contractId }: Props) {
  const { toast } = useToast();
  const [rows, setRows] = useState<GuestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const r = await fetch(`/api/contracts/${contractId}/guests`, { credentials: "include" });
      if (r.ok) setRows((await r.json()) as GuestRow[]);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function invite(): Promise<void> {
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/contracts/${contractId}/guests`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), expiresInDays: days }),
      });
      if (r.ok) {
        setEmail("");
        toast({ title: "Invitation sent", description: `Guest reviewer invited: ${email.trim()}` });
        await reload();
      } else {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        toast({
          title: "Could not invite",
          description: body.error ?? `Server returned ${r.status}`,
          variant: "destructive",
        });
      }
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: number): Promise<void> {
    const r = await fetch(`/api/contracts/${contractId}/guests/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (r.ok) {
      toast({ title: "Invitation revoked" });
      await reload();
    }
  }

  async function resend(id: number): Promise<void> {
    const r = await fetch(`/api/contracts/${contractId}/guests/${id}/resend`, {
      method: "POST",
      credentials: "include",
    });
    if (r.ok) {
      toast({ title: "Invitation re-sent" });
    }
  }

  return (
    <Card data-testid="contract-guests-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Scale className="h-4 w-4" />
          Guest Legal Reviewers
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Invite an outside attorney to review this single contract. Guests can read,
          comment, and propose redlines — they cannot see anything else in your workspace
          and never consume a paid seat. Invitations default to 30 days and can be revoked
          at any time.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="attorney@example.com"
            data-testid="guest-invite-email"
          />
          <Input
            type="number"
            min={1}
            max={90}
            value={days}
            onChange={(e) => setDays(Math.max(1, Math.min(90, Number(e.target.value) || 30)))}
            className="sm:w-28"
            aria-label="Expires in days"
            data-testid="guest-invite-days"
          />
          <Button onClick={invite} disabled={!email.trim() || busy} data-testid="guest-invite-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
            Send invite
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No guest reviewers invited yet.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((g) => (
              <div
                key={g.id}
                className="flex items-center gap-2 border rounded-md p-2 text-sm"
                data-testid={`guest-row-${g.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{g.email}</div>
                  <div className="text-xs text-muted-foreground">
                    Invited {new Date(g.invitedAt).toLocaleDateString()}
                    {g.acceptedAt ? ` · joined ${new Date(g.acceptedAt).toLocaleDateString()}` : ""}
                    {" · expires "}{new Date(g.expiresAt).toLocaleDateString()}
                  </div>
                </div>
                <Badge variant={STATUS_BADGE[g.status]}>{g.status}</Badge>
                {g.status === "pending" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => resend(g.id)}
                    data-testid={`guest-resend-${g.id}`}
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                ) : null}
                {g.status === "pending" || g.status === "active" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => revoke(g.id)}
                    data-testid={`guest-revoke-${g.id}`}
                  >
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
